import compact from 'lodash/compact'
import first from 'lodash/first'
import map from 'lodash/map'
import React, { FC, useMemo } from 'react'

import { MediaGalleryBannerBlock } from '@Blocks/MediaGalleryBlock/MediaGalleryBannerBlock/MediaGalleryBannerBlock'
import { MediaGalleryDefaultBlock } from '@Blocks/MediaGalleryBlock/MediaGalleryDefaultBlock/MediaGalleryDefaultBlock'
import { VideoProps } from '@Components/CMS/Video'
import { MediaGalleryCardProps, MediaGalleryVideo } from '@Components/MediaGalleryCard'
import { Block, ImageResource, LinkField, Page, VideoResource } from '@Generated/graphql'
import { getJSONFieldValue, getLinkFieldValue, getPageReferences, getProductFieldValue, getReferenceFieldValue } from '@Globals/CMS/FieldHelpers'
import { cmsProductToGridProduct } from '@Globals/CMS/ProductHelpers'
import { Image } from '@Globals/Image'

interface MediaGalleryBlockData {
  bannerCard?: MediaGalleryCardProps
  cards: MediaGalleryCardProps[]
  layout: 'Column Offset' | 'Row' | 'Banner Slider'
}

const getVideoFromReference = (mediaReference) => {
  if (mediaReference.__typename !== 'VideoResource') {
    return null
  }
  return {
    videoId: mediaReference?.asset?.key || '',
    videoMobileId: mediaReference?.mobileAsset?.key || '',
    videoType: mediaReference?.asset?.type as VideoProps['videoType'],
    settings: mediaReference?.settings || {},
  }
}

const getImageFromReference = (mediaReference) => {
  if (mediaReference.__typename !== 'ImageResource') {
    return null
  }
  return {
    dimensions: {
      height: mediaReference?.image?.dimensions?.height || 0,
      width: mediaReference?.image?.dimensions?.width || 0,
    },
    url: mediaReference?.image?.url || '',
  }
}
const getImageMobileFromReference = (mediaReference) => {
  if (mediaReference.__typename !== 'ImageResource') {
    return null
  }
  return {
    dimensions: {
      height: mediaReference?.mobileImage?.dimensions?.height || 0,
      width: mediaReference?.mobileImage?.dimensions?.width || 0,
    },
    url: mediaReference?.mobileImage?.url || '',
  }
}

const getImageLinkField = (mediaReference) => {
  if (mediaReference.__typename !== 'ImageResource') {
    return null
  }
  return getLinkFieldValue(mediaReference.fields || [], 'link') || undefined
}

const getProducts = mediaReference => compact(map(mediaReference.captionOptions?.references, (page: Page) => {
  const productCMSData = getProductFieldValue(page.fields || [], 'product')
  if (!productCMSData) {
    return null
  }
  return cmsProductToGridProduct(productCMSData)
}))

const getBlockData = (mediaGalleryBlock: Block): MediaGalleryBlockData => {
  const mediaReferences = getReferenceFieldValue<ImageResource | VideoResource>(mediaGalleryBlock.fields || [], 'reference') || []
  const bannerResources = getReferenceFieldValue<ImageResource | VideoResource>(mediaGalleryBlock.fields || [], 'bannerImage') || []

  const getMediaGalleryCardProps: (references) => MediaGalleryCardProps[] = references => compact(map(references, (mediaReference) => {
    const image: Image | null = getImageFromReference(mediaReference)
    const mobileImage: Image | null = getImageMobileFromReference(mediaReference)
    const link: LinkField | null | undefined = getImageLinkField(mediaReference)
    const video: MediaGalleryVideo | null = getVideoFromReference(mediaReference)

    const linkReference = first(getPageReferences(link?.references || []))
    return {
      caption: mediaReference.captionOptions?.caption?.toString(),
      display: getJSONFieldValue(mediaGalleryBlock.fields || [], 'display') || [],
      icon: mediaReference.overlayOptions?.icon || '',
      id: mediaReference.id,
      image,
      link: link || undefined,
      linkType: linkReference?.type || undefined,
      linkID: linkReference?.id || undefined,
      mobileImage,
      products: getProducts(mediaReference),
      someHandle: mediaReference.overlayOptions?.caption || '',
      video,
    }
  }))

  return {
    bannerCard: first(getMediaGalleryCardProps(bannerResources)),
    cards: getMediaGalleryCardProps(mediaReferences),
    layout: getJSONFieldValue(mediaGalleryBlock.fields || [], 'layout') || 'Row',
  }
}

enum MediaGalleryLayout {
  COLUMN_OFFSET = 'Column Offset',
}

interface MediaGalleryBlockProps {
  id?: string
  isInjection?: boolean
  mediaGalleryBlock: Block
}

const MediaGalleryBlock: FC<MediaGalleryBlockProps> = ({
  id,
  isInjection,
  mediaGalleryBlock,
}) => {
  const {
    bannerCard,
    cards,
    layout,
  } = useMemo(() => getBlockData(mediaGalleryBlock), [mediaGalleryBlock])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const split = useMemo(() => layout.toLowerCase() === MediaGalleryLayout.COLUMN_OFFSET.toLowerCase(), [])

  if (layout === 'Banner Slider') {
    return <MediaGalleryBannerBlock bannerCard={bannerCard} cards={cards} id={id} split={split} />
  }
  return <MediaGalleryDefaultBlock cards={cards} fullWidth id={id} isInjection={isInjection} split={split} zigzag={!!split} />
}

export default MediaGalleryBlock
